import { useEffect, useMemo, useRef, useState } from "react";
import { Download, Loader2, Pencil, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, downloadFile, errMsg } from "@/lib/api";
import { PageHeader, fmtDate } from "@/components/Bits";
import { KbDuplicates } from "@/components/KbDuplicates";
import { KbCleanup } from "@/components/KbCleanup";

const BLANK = { title: "", category: "General", tags: "", content: "", audience: "shared", status: "live" };
const AUD_LABEL = { shared: "Shared", cpg: "CPG", hospitality: "Hospitality" };

const Editor = ({ entry, onClose, onSaved }) => {
  const [f, setF] = useState(entry ? { ...entry, tags: (entry.tags || []).join(", ") } : BLANK);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { title: f.title, category: f.category, content: f.content, audience: f.audience || "shared", status: f.status || "live", tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      const { data } = entry?.id ? await api.put(`/admin/kb/${entry.id}`, body) : await api.post("/admin/kb", body);
      toast.success("Saved to the knowledge bank");
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl bg-card border-border" data-testid="kb-editor">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{entry?.id ? "Edit entry" : "New knowledge bank entry"}</DialogTitle></DialogHeader>
        <Input value={f.title} onChange={set("title")} placeholder="Title, e.g. 'Filling quiet Mondays'" data-testid="kb-title-input" className="bg-white" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Input value={f.category} onChange={set("category")} placeholder="Category" data-testid="kb-category-input" className="bg-white" />
          <Input value={f.tags} onChange={set("tags")} placeholder="Tags, comma separated" data-testid="kb-tags-input" className="bg-white" />
        </div>
        <select value={f.audience || "shared"} onChange={set("audience")} data-testid="kb-audience-select" className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          {Object.entries(AUD_LABEL).map(([v, l]) => <option key={v} value={v}>{l === "Shared" ? "Shared (both audiences)" : `${l} only`}</option>)}
        </select>
        <select value={f.status || "live"} onChange={set("status")} data-testid="kb-status-select" className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          <option value="live">Live: the AI uses this entry</option>
          <option value="draft">Draft: hidden from the AI until published</option>
        </select>
        <Textarea rows={12} value={f.content} onChange={set("content")} placeholder="Your advice, frameworks, examples, numbers…" data-testid="kb-content-input" className="bg-white" />
        <Button onClick={save} data-testid="kb-save-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white w-fit">Save entry</Button>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminKB() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [syncs, setSyncs] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const fileRef = useRef();

  const load = () => {
    api.get("/admin/kb").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/kb/syncs").then((r) => setSyncs(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const sync = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/kb/import", fd);
      toast.success(`Synced ${data.imported} sections from ${data.doc_name}${data.skipped ? `. Skipped ${data.skipped} already in the Knowledge bank word for word.` : ""}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSyncing(false); fileRef.current.value = ""; }
  };
  const exportMd = () => downloadFile("/admin/kb/export", "knowledge-bank-export.md").catch((e) => toast.error(errMsg(e)));

  const shown = useMemo(() => [...items].sort((a, b) => (b.status === "draft") - (a.status === "draft")).filter((i) => `${i.title} ${i.category} ${i.content}`.toLowerCase().includes(q.toLowerCase())), [items, q]);

  const saved = (d) => {
    setItems((l) => (l.some((x) => x.id === d.id) ? l.map((x) => (x.id === d.id ? d : x)) : [d, ...l]));
    setEditing(null);
  };
  const publish = async (e) => {
    try {
      const { data } = await api.put(`/admin/kb/${e.id}`, { title: e.title, category: e.category, tags: e.tags || [], content: e.content, audience: e.audience || "shared", status: "live" });
      setItems((l) => l.map((x) => (x.id === data.id ? data : x)));
      toast.success("Published. The AI will now use this entry.");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const drafts = items.filter((i) => i.status === "draft").length;
  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/admin/kb/${id}`);
    setItems((l) => l.filter((x) => x.id !== id));
    toast.success("Entry deleted");
  };

  return (
    <div>
      <PageHeader eyebrow={`Knowledge bank · ${items.length} entries`} title="What the AI knows.">
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => fileRef.current.click()} disabled={syncing} variant="outline" data-testid="kb-sync-btn" className="rounded-full">
            {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}Sync from .md
          </Button>
          <input ref={fileRef} type="file" accept=".md,.txt,.markdown" hidden data-testid="kb-sync-input" onChange={(e) => sync(e.target.files[0])} />
          <Button onClick={exportMd} variant="outline" data-testid="kb-export-btn" className="rounded-full"><Download className="h-4 w-4 mr-2" />Export .md</Button>
          <KbCleanup onDone={load} />
          <KbDuplicates onDone={load} />
          <Button onClick={() => setEditing({})} data-testid="kb-new-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white"><Plus className="h-4 w-4 mr-2" />New entry</Button>
        </div>
      </PageHeader>
      <div className="rounded-2xl border border-border bg-card p-5 mb-8 text-sm text-muted-foreground max-w-3xl" data-testid="kb-sync-info">
        <span className="text-foreground">Keeping it in step with Claude:</span> update your knowledge bank .md in Claude, then click <em>Sync from .md</em>. Sections from that file are replaced; entries you added here or from member questions are kept. Use <em>Export .md</em> to copy everything back into Claude.
        {syncs[0] && <div className="mt-2 font-mono text-xs" data-testid="kb-last-sync">Last sync: {syncs[0].doc_name} · {syncs[0].entries} sections{syncs[0].skipped ? ` (${syncs[0].skipped} duplicates skipped)` : ""} · {fmtDate(syncs[0].synced_at)}</div>}
      </div>
      {drafts > 0 && <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-4 mb-6 text-sm max-w-3xl" data-testid="kb-drafts-banner"><span className="font-medium">{drafts} draft {drafts === 1 ? "entry" : "entries"} waiting for you.</span> Drafts are hidden from the AI. Edit them in your own words, then click Publish.</div>}
      <div className="relative mb-8 max-w-md">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search entries" data-testid="kb-search-input" className="pl-9 bg-white" />
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {shown.map((e) => (
          <article key={e.id} className="rounded-2xl border border-border bg-card p-6" data-testid={`kb-entry-${e.id}`}>
            <div className="flex justify-between gap-3">
              <div className="text-[11px] font-mono uppercase tracking-wider text-pinkb">{e.category} · <span className="text-navy" data-testid={`kb-audience-${e.id}`}>{AUD_LABEL[e.audience || "shared"]}</span>{e.source === "escalation" && " · from a member question"}{e.status === "draft" && <span className="ml-2 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5" data-testid={`kb-draft-badge-${e.id}`}>Draft</span>}{e.source === "import" && ` · ${e.doc_name}`}</div>
              <div className="flex gap-1 items-start">
                {e.status === "draft" && <button onClick={() => publish(e)} data-testid={`kb-publish-${e.id}`} className="text-xs rounded-full bg-navy text-white px-3 py-1 hover:bg-navy/90 transition-colors">Publish</button>}
                <button onClick={() => setEditing(e)} data-testid={`kb-edit-${e.id}`} className="p-1.5 text-muted-foreground hover:text-pinkb"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(e.id)} data-testid={`kb-delete-${e.id}`} className="p-1.5 text-muted-foreground hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <h3 className="mt-2 text-lg font-semibold">{e.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground line-clamp-4 whitespace-pre-line">{e.content}</p>
            <div className="mt-4 flex flex-wrap gap-1.5 items-center">
              {(e.tags || []).map((t) => <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-border text-muted-foreground">{t}</span>)}
              <span className="ml-auto text-[11px] text-muted-foreground">{fmtDate(e.updated_at)}</span>
            </div>
          </article>
        ))}
      </div>
      {editing && <Editor entry={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={saved} />}
    </div>
  );
}
